import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { OpenableApp } from '@api';

import { useAuth } from '@shared/common/providers/AuthProvider';

import { useUserOpenableAppListInSpaceQuery } from '@store:web/components/pages/SpacesPage/SpacesAppListTabPanel';
import { useAddAppInSpaces } from '@store:web/components/pages/SpacesPage/useAddAppInSpaces';
import { useRemoveAppFromSpace } from '@store:web/components/pages/SpacesPage/useRemoveAppFromSpaces';

const NON_AUTH_USER_APP_IN_SPACE_LIMIT = 3;

export const useAppInSpace = (app: OpenableApp) => {
  const { isAuth } = useAuth();
  // NOTE: we need a way to determine if app in spaces,
  // to do so we are getting list of app ids and check if it has current app id
  // In future we need to fix it and extend (add inSpace flat) endpoint that fetches app meta data
  const { data: appList, isPending: isLoading } = useUserOpenableAppListInSpaceQuery();
  const lastUpdatedValue = useMemo(
    () => !!appList?.find((appInSpace) => appInSpace.id === app.id),
    [app.id, appList],
  );
  const initialValue = useRef(lastUpdatedValue).current;
  const [value, setValue] = useState(lastUpdatedValue);
  const addApp = useAddAppInSpaces();
  const removeApp = useRemoveAppFromSpace();
  const isNonAuthAppsInSpaceLimitReached = isAuth
    ? false
    : (appList?.length || 0) >= NON_AUTH_USER_APP_IN_SPACE_LIMIT;
  const toggleAppInCurrentUserSpace = useCallback(
    (inSpace: boolean) => {
      if (inSpace) {
        addApp(app);
      } else {
        removeApp(app.id);
      }
    },
    [addApp, removeApp, app],
  );
  const toggle = useCallback(() => {
    setValue((oldValue) => {
      if (isAuth) {
        return !oldValue;
      }

      if (oldValue) {
        return false;
      } else {
        if (isNonAuthAppsInSpaceLimitReached) {
          toast.error(
            `Unable add app in spaces. Spaces limit for unregistered user is ${NON_AUTH_USER_APP_IN_SPACE_LIMIT}`,
          );

          return false;
        }

        return true;
      }
    });
  }, [isAuth, isNonAuthAppsInSpaceLimitReached]);

  useLayoutEffect(() => {
    setValue(lastUpdatedValue);
  }, [lastUpdatedValue]);

  useEffect(() => {
    if (isLoading || value === lastUpdatedValue) {
      return;
    }

    const timeoutId = setTimeout(() => {
      toggleAppInCurrentUserSpace(value);
    }, 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [value, lastUpdatedValue, toggleAppInCurrentUserSpace]);

  return {
    value,
    initialValue,
    toggle,
  };
};
